import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Contact, Occasion, OccasionType, RelationshipType, CardType } from '../../../../shared/src/types/database';
import ContactForm, { type ContactFormValues } from '../contacts/ContactForm';
import OccasionForm from '../occasions/OccasionForm';
import CardDesignPicker from '../cards/CardDesignPicker';
import Modal        from '../ui/Modal';
import { apiFetch } from '../../lib/api';
import { useAuth }  from '../../context/AuthContext';

const TIER_CONTACT_LIMITS: Record<number, number | null> = { 1: 5, 2: 20, 3: null };

type ContactWithOccasions = Contact & { occasions: Occasion[] };

interface PeopleSectionProps {
  contacts:           ContactWithOccasions[];
  onContactAdded:     (c: Contact, autoOccasions?: Occasion[]) => void;
  onContactUpdated:   (c: Contact)  => void;
  onContactDeleted:   (id: string)  => void;
  onOccasionAdded:    (o: Occasion) => void;
  onOccasionUpdated?: (o: Occasion) => void;
  onOccasionDeleted?: (id: string)  => void;
}

// ─── Label maps ──────────────────────────────────────────────────────────────

const RELATIONSHIP_LABELS: Record<RelationshipType, string> = {
  mom:         'Mom',
  dad:         'Dad',
  spouse:      'Spouse',
  husband:     'Husband',
  wife:        'Wife',
  partner:     'Partner',
  child:       'Child',
  sibling:     'Sibling',
  grandparent: 'Grandparent',
  best_friend: 'Best Friend',
  friend:      'Friend',
  coworker:    'Coworker',
  other:       'Other',
};

const AUTO_OCCASION_LABELS: Partial<Record<OccasionType, string>> = {
  mothers_day: "Mother's Day",
  fathers_day: "Father's Day",
  valentines:  "Valentine's Day",
};

const OCCASION_EMOJIS: Record<OccasionType, string> = {
  birthday:        '🎂',
  anniversary:     '💍',
  christmas:       '🎄',
  valentines:      '💝',
  mothers_day:     '🌸',
  fathers_day:     '👔',
  graduation:      '🎓',
  thinking_of_you: '💌',
  custom:          '✨',
};

function nextOccasionLabel(occasions: Occasion[]): string | null {
  const today = new Date().toISOString().split('T')[0];
  const next = occasions
    .filter((o) => o.next_send_date && o.next_send_date >= today)
    .sort((a, b) => (a.next_send_date ?? '').localeCompare(b.next_send_date ?? ''))[0];
  if (!next) return null;
  const emoji = OCCASION_EMOJIS[next.occasion_type] ?? '📅';
  const date  = new Date(next.next_send_date! + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'short', day: 'numeric',
  });
  return `${emoji} ${date}`;
}

// ─── Context menu ─────────────────────────────────────────────────────────────

function ContactMenu({
  contact,
  onEdit,
  onAddOccasion,
  onDelete,
}: {
  contact:       ContactWithOccasions;
  onEdit:        () => void;
  onAddOccasion: () => void;
  onDelete:      () => void;
}) {
  const [open,          setOpen         ] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (confirmDelete) {
    return (
      <div className="flex flex-col items-end gap-1.5">
        <span className="font-body text-xs text-gray-500">Remove {contact.name}?</span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => { onDelete(); setConfirmDelete(false); }}
            className="text-xs font-semibold font-body px-2 py-1 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
          >
            Yes, remove
          </button>
          <button
            type="button"
            onClick={() => setConfirmDelete(false)}
            className="text-xs font-body text-gray-400 hover:text-gray-600"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="rounded-full p-1.5 text-gray-600 hover:text-navy hover:bg-gray-100 transition-colors"
        aria-label="Contact options"
      >
        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="5"  r="1.5" />
          <circle cx="12" cy="12" r="1.5" />
          <circle cx="12" cy="19" r="1.5" />
        </svg>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-20 w-44 rounded-[12px] bg-white shadow-warm-lg border border-gray-100 py-1.5 overflow-hidden">
            <button
              type="button"
              onClick={() => { setOpen(false); onEdit(); }}
              className="flex w-full items-center gap-2 px-3 py-2 text-xs font-body text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <svg className="h-3.5 w-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit contact
            </button>
            <button
              type="button"
              onClick={() => { setOpen(false); onAddOccasion(); }}
              className="flex w-full items-center gap-2 px-3 py-2 text-xs font-body text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <svg className="h-3.5 w-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Add occasion
            </button>
            <div className="border-t border-gray-50 my-1" />
            <button
              type="button"
              onClick={() => { setOpen(false); setConfirmDelete(true); }}
              className="flex w-full items-center gap-2 px-3 py-2 text-xs font-body text-red-500 hover:bg-red-50 transition-colors"
            >
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Remove
            </button>
          </div>
        </>
      )}
    </div>
  );
}

const OCCASION_META: Record<OccasionType, { label: string; emoji: string }> = {
  birthday:        { label: 'Birthday',        emoji: '🎂' },
  anniversary:     { label: 'Anniversary',     emoji: '💍' },
  christmas:       { label: 'Christmas',       emoji: '🎄' },
  valentines:      { label: "Valentine's Day", emoji: '💝' },
  mothers_day:     { label: "Mother's Day",    emoji: '🌸' },
  fathers_day:     { label: "Father's Day",    emoji: '👔' },
  graduation:      { label: 'Graduation',      emoji: '🎓' },
  thinking_of_you: { label: 'Thinking of You', emoji: '💌' },
  custom:          { label: 'Custom',          emoji: '✨' },
};

// ─── Occasion card-design picker modal ───────────────────────────────────────

function ContactOccasionModal({
  occasion,
  contactName,
  onClose,
  onSaved,
  onDeleted,
}: {
  occasion:    Occasion;
  contactName: string;
  onClose:     () => void;
  onSaved:     (updated: Occasion) => void;
  onDeleted?:  (id: string) => void;
}) {
  const { profile } = useAuth();
  const meta  = OCCASION_META[occasion.occasion_type as OccasionType] ?? { label: occasion.occasion_type, emoji: '📅' };
  const label = occasion.occasion_type === 'custom' && occasion.custom_occasion_name
    ? occasion.custom_occasion_name
    : OCCASION_LABEL_MAP[occasion.occasion_type as OccasionType] ?? occasion.occasion_type;

  const [selectedDesignId, setSelectedDesignId] = useState<string | null>(occasion.card_design_id ?? null);
  const [saving,         setSaving        ] = useState(false);
  const [saved,          setSaved         ] = useState(false);
  const [confirmRemove,  setConfirmRemove ] = useState(false);
  const [removing,       setRemoving      ] = useState(false);

  const dateStr = occasion.next_send_date
    ? new Date(occasion.next_send_date + 'T00:00:00').toLocaleDateString('en-US', {
        month: 'long', day: 'numeric', year: 'numeric',
      })
    : null;

  async function handleSave() {
    if (selectedDesignId === (occasion.card_design_id ?? null)) { onClose(); return; }
    setSaving(true);
    try {
      const json = await apiFetch<{ occasion: Occasion }>(`/api/occasions/${occasion.id}`, {
        method: 'PUT',
        body:   JSON.stringify({ card_design_id: selectedDesignId }),
      });
      setSaved(true);
      onSaved(json.occasion);
      setTimeout(onClose, 700);
    } catch {
      setSaving(false);
    }
  }

  async function handleRemove() {
    setRemoving(true);
    try {
      await apiFetch(`/api/occasions/${occasion.id}`, { method: 'DELETE' });
      onDeleted?.(occasion.id);
      onClose();
    } catch {
      setRemoving(false);
      setConfirmRemove(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ background: 'rgba(26,20,16,0.45)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <div
        className="relative bg-white w-full sm:max-w-md rounded-t-[24px] sm:rounded-[24px]
                   shadow-warm-xl max-h-[90dvh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex-shrink-0 px-6 pt-5 pb-4 border-b border-gray-100 flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <span className="text-xl">{meta.emoji}</span>
              <h3 className="font-heading text-lg font-semibold text-navy">{label}</h3>
            </div>
            <p className="font-body text-sm text-gray-500">for {contactName}</p>
            {dateStr && (
              <p className="font-body text-xs text-sage mt-1">{dateStr}</p>
            )}
          </div>
          <button onClick={onClose} className="flex-shrink-0 rounded-full p-1.5 text-gray-400 hover:text-navy hover:bg-gray-100 transition-colors" aria-label="Close">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-3">
          <div>
            <p className="font-body text-sm font-semibold text-navy mb-1">
              {occasion.card_design_id ? 'Card design' : 'Pick a card design'}
            </p>
            {!occasion.card_design_id && (
              <p className="font-body text-xs text-gray-400 mb-3">
                Choose a design now, we'll use it when this occasion comes up.
              </p>
            )}
            <CardDesignPicker
              occasionType={occasion.occasion_type}
              selected={selectedDesignId}
              onChange={setSelectedDesignId}
              cardClosing={profile?.card_closing ?? 'With love,'}
              cardFromLine={profile?.card_from_line ?? profile?.first_name ?? ''}
            />
          </div>
        </div>

        {/* Footer */}
        {confirmRemove ? (
          <div className="flex-shrink-0 px-6 py-4 border-t border-gray-100">
            <p className="font-body text-sm text-center text-gray-600 mb-3">
              Remove this occasion for {contactName}?
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setConfirmRemove(false)}
                disabled={removing}
                className="btn-secondary flex-1 text-sm"
              >
                Keep it
              </button>
              <button
                type="button"
                onClick={handleRemove}
                disabled={removing}
                className="flex-1 text-sm rounded-xl bg-red-50 text-red-600 font-body font-semibold
                           hover:bg-red-100 transition-colors px-4 py-2.5 disabled:opacity-60"
              >
                {removing ? 'Removing…' : 'Yes, remove'}
              </button>
            </div>
          </div>
        ) : (
          <div className="flex-shrink-0 px-6 py-4 border-t border-gray-100">
            <div className="flex gap-3">
              <button onClick={onClose} disabled={saving} className="btn-secondary flex-1 text-sm">Cancel</button>
              <button onClick={handleSave} disabled={saving || saved} className="btn-primary flex-1 text-sm disabled:opacity-60">
                {saved ? '✓ Saved!' : saving ? 'Saving…' : 'Save'}
              </button>
            </div>
            {onDeleted && (
              <button
                type="button"
                onClick={() => setConfirmRemove(true)}
                className="w-full mt-2 font-body text-xs text-red-400 hover:text-red-600 transition-colors text-center py-1"
              >
                Remove this occasion
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Single contact card ──────────────────────────────────────────────────────

const OCCASION_EMOJIS_MAP: Record<OccasionType, string> = {
  birthday:        '🎂',
  anniversary:     '💍',
  christmas:       '🎄',
  valentines:      '💝',
  mothers_day:     '🌸',
  fathers_day:     '👔',
  graduation:      '🎓',
  thinking_of_you: '💌',
  custom:          '✨',
};

const OCCASION_LABEL_MAP: Record<OccasionType, string> = {
  birthday:        'Birthday',
  anniversary:     'Anniversary',
  christmas:       'Christmas',
  valentines:      "Valentine's Day",
  mothers_day:     "Mother's Day",
  fathers_day:     "Father's Day",
  graduation:      'Graduation',
  thinking_of_you: 'Thinking of You',
  custom:          'Custom',
};

function ContactCard({
  contact,
  onEdit,
  onAddOccasion,
  onDelete,
  onFavoriteToggled,
  onOccasionUpdated,
  onOccasionDeleted,
}: {
  contact:             ContactWithOccasions;
  onEdit:              () => void;
  onAddOccasion:       () => void;
  onDelete:            () => void;
  onFavoriteToggled:   (updated: Contact) => void;
  onOccasionUpdated?:  (updated: Occasion) => void;
  onOccasionDeleted?:  (id: string) => void;
}) {
  const nextLabel     = nextOccasionLabel(contact.occasions);
  const occasionCount = contact.occasions.length;
  const location      = contact.address_city
    ? `${contact.address_city}${contact.address_state ? `, ${contact.address_state}` : ''}`
    : null;

  const [isFavorite,    setIsFavorite   ] = useState(contact.is_favorite ?? false);
  const [favLoading,    setFavLoading   ] = useState(false);
  const [showOccasions, setShowOccasions] = useState(false);
  const [detailOccasion,setDetailOccasion] = useState<Occasion | null>(null);

  // Sync local state when parent contact prop changes (fixes can't-unfavorite bug)
  useEffect(() => { setIsFavorite(contact.is_favorite ?? false); }, [contact.is_favorite]);

  async function toggleFavorite(e: React.MouseEvent) {
    e.stopPropagation();
    if (favLoading) return;
    const newVal = !isFavorite;
    setIsFavorite(newVal);
    setFavLoading(true);
    try {
      const json = await apiFetch<{ contact: Contact }>(`/api/contacts/${contact.id}`, {
        method: 'PUT',
        body:   JSON.stringify({ is_favorite: newVal }),
      });
      onFavoriteToggled(json.contact);
    } catch {
      setIsFavorite(!newVal); // revert on error
    } finally {
      setFavLoading(false);
    }
  }

  const initials = contact.name.charAt(0).toUpperCase();

  return (
    <div className="bg-white rounded-[20px] shadow-warm p-5 flex flex-col gap-3 hover:shadow-warm-lg transition-shadow">
      {/* Header row */}
      <div className="flex items-start justify-between gap-2">
        {/* Avatar + name */}
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex-shrink-0 h-10 w-10 rounded-full overflow-hidden bg-sage/15 flex items-center justify-center">
            {contact.avatar_url ? (
              <img
                src={contact.avatar_url}
                alt={contact.name}
                className="h-full w-full object-cover"
              />
            ) : (
              <span className="font-heading text-sm font-semibold text-sage">{initials}</span>
            )}
          </div>
          <div className="min-w-0">
            <p className="font-heading text-base font-semibold text-navy leading-snug truncate">
              {contact.name}
            </p>
            <div className="flex items-center gap-1.5 flex-wrap">
              {contact.relationship_type && (
                <span className="font-body text-xs text-sage">
                  {RELATIONSHIP_LABELS[contact.relationship_type] ?? contact.relationship_type}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Actions: star + menu */}
        <div className="flex items-center gap-0.5 flex-shrink-0">
          <button
            type="button"
            onClick={toggleFavorite}
            disabled={favLoading}
            aria-label={isFavorite ? 'Remove from favorites' : 'Mark as favorite'}
            className="rounded-full p-1.5 transition-colors hover:bg-amber-50 disabled:opacity-50"
          >
            <svg
              className={`h-4 w-4 transition-colors ${isFavorite ? 'text-amber-400 fill-amber-400' : 'text-gray-300 fill-none'}`}
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={isFavorite ? 0 : 2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
            </svg>
          </button>

          <ContactMenu
            contact={contact}
            onEdit={onEdit}
            onAddOccasion={onAddOccasion}
            onDelete={onDelete}
          />
        </div>
      </div>

      {/* Location */}
      {location && (
        <p className="font-body text-xs text-gray-400 flex items-center gap-1">
          <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          {location}
          {contact.address_verified && (
            <svg className="h-3 w-3 text-sage ml-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          )}
        </p>
      )}

      {/* Footer */}
      <div className="mt-auto pt-1 border-t border-gray-50">
        <div className="flex items-center justify-between">
          {occasionCount === 0 ? (
            <p className="font-body text-xs text-gray-400">No occasions yet</p>
          ) : (
            <button
              type="button"
              onClick={() => setShowOccasions(!showOccasions)}
              className="flex items-center gap-1 font-body text-xs font-semibold text-sage hover:text-navy transition-colors"
            >
              <svg
                className={`h-3 w-3 transition-transform ${showOccasions ? 'rotate-90' : ''}`}
                fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
              {occasionCount === 1 ? '1 occasion' : `${occasionCount} occasions`}
            </button>
          )}
          {nextLabel && !showOccasions && (
            <p className="font-body text-xs text-navy font-medium">{nextLabel}</p>
          )}
        </div>

        {/* Occasions dropdown */}
        {showOccasions && occasionCount > 0 && (
          <div className="mt-2 flex flex-col gap-1.5">
            {[...contact.occasions]
              .sort((a, b) => (a.next_send_date ?? '').localeCompare(b.next_send_date ?? ''))
              .map((o) => {
                const emoji = OCCASION_EMOJIS_MAP[o.occasion_type] ?? '📅';
                const label = o.occasion_type === 'custom' && o.custom_occasion_name
                  ? o.custom_occasion_name
                  : (OCCASION_LABEL_MAP[o.occasion_type] ?? o.occasion_type);
                const dateStr = o.next_send_date
                  ? new Date(o.next_send_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                  : null;
                const hasDesign = !!o.card_design_id;
                return (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => setDetailOccasion(o)}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-xl
                               border border-sage/20 bg-sage/5 hover:bg-sage/10 hover:border-sage/40
                               transition-all text-left group"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-base leading-none flex-shrink-0">{emoji}</span>
                      <div className="min-w-0">
                        <span className="font-body text-xs font-semibold text-navy block truncate">{label}</span>
                        <div className="flex items-center gap-1 mt-0.5">
                          {o.recurring && (
                            <span className="font-body text-[9px] text-sage">yearly</span>
                          )}
                          {hasDesign ? (
                            <span className="font-body text-[9px] text-sage">· card selected ✓</span>
                          ) : (
                            <span className="font-body text-[9px] text-gray-400 group-hover:text-sage transition-colors">· tap to pick card</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {dateStr && (
                        <span className="font-body text-[10px] text-gray-400">{dateStr}</span>
                      )}
                      <svg className="h-3 w-3 text-sage/40 group-hover:text-sage transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </button>
                );
              })}
          </div>
        )}
      </div>

      {/* Occasion card-picker modal */}
      {detailOccasion && (
        <ContactOccasionModal
          occasion={detailOccasion}
          contactName={contact.name}
          onClose={() => setDetailOccasion(null)}
          onSaved={(updated) => {
            setDetailOccasion(null);
            onOccasionUpdated?.(updated);
          }}
          onDeleted={(id) => {
            setDetailOccasion(null);
            onOccasionDeleted?.(id);
          }}
        />
      )}
    </div>
  );
}

// ─── PeopleSection ────────────────────────────────────────────────────────────

export default function PeopleSection({
  contacts,
  onContactAdded,
  onContactUpdated,
  onContactDeleted,
  onOccasionAdded,
  onOccasionUpdated,
  onOccasionDeleted,
}: PeopleSectionProps) {
  const navigate           = useNavigate();
  const { profile }        = useAuth();
  const tier               = (profile?.tier ?? null) as number | null;
  const contactLimit       = tier !== null ? (TIER_CONTACT_LIMITS[tier] ?? null) : null;
  const atLimit            = contactLimit !== null && contacts.length >= contactLimit;
  const nearLimit          = contactLimit !== null && !atLimit && contacts.length === contactLimit - 1;

  const [addOpen,          setAddOpen         ] = useState(false);
  const [editTarget,       setEditTarget       ] = useState<ContactWithOccasions | null>(null);
  const [occasionTarget,   setOccasionTarget   ] = useState<ContactWithOccasions | null>(null);
  const [autoHint,         setAutoHint         ] = useState<string | null>(null);
  const [deleteError,      setDeleteError      ] = useState<string | null>(null);

  function handleAddPersonClick() {
    if (atLimit) {
      navigate('/pricing?reason=contact_limit');
    } else {
      setAddOpen(true);
    }
  }

  async function handleDelete(contactId: string) {
    setDeleteError(null);
    try {
      await apiFetch(`/api/contacts/${contactId}`, { method: 'DELETE' });
      onContactDeleted(contactId);
    } catch (err) {
      console.error('[PeopleSection] delete error:', err);
      setDeleteError('Could not remove contact. Please try again.');
      setTimeout(() => setDeleteError(null), 4000);
    }
  }

  function editInitial(c: ContactWithOccasions): Partial<ContactFormValues> {
    return {
      name:                  c.name,
      relationship_type:     c.relationship_type ?? '',
      address_street:        c.address_street    ?? '',
      address_city:          c.address_city      ?? '',
      address_state:         c.address_state     ?? '',
      address_postal:        c.address_postal    ?? '',
      address_country:       c.address_country,
      personalization_notes: c.personalization_notes ?? '',
      card_style_preference: c.card_style_preference ?? '',
      card_type:             (c.card_type as CardType) ?? 'folded',
    };
  }

  function editInitialBirthday(c: ContactWithOccasions) {
    const bday = c.occasions.find((o) => o.occasion_type === 'birthday');
    if (!bday?.occasion_date) return null;
    const [yr, mo, dy] = bday.occasion_date.split('-');
    const year = bday.birth_year
      ? String(bday.birth_year)
      : (yr && yr !== '1900' ? yr : '');
    return { month: mo ?? '', day: dy ?? '', year, recurring: bday.recurring ?? true };
  }

  function editInitialAnniversary(c: ContactWithOccasions) {
    const anniv = c.occasions.find((o) => o.occasion_type === 'anniversary');
    if (!anniv?.occasion_date) return null;
    const [yr, mo, dy] = anniv.occasion_date.split('-');
    return { month: mo ?? '', day: dy ?? '', year: yr !== '1900' ? (yr ?? '') : '' };
  }

  return (
    <section>
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="font-heading text-xl font-semibold text-navy">
          Your People
        </h2>
        <button
          type="button"
          onClick={handleAddPersonClick}
          className="flex items-center gap-1.5 font-body text-xs font-semibold text-sage hover:text-sage/80 transition-colors"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          {atLimit ? 'Upgrade to add more' : 'Add person'}
        </button>
      </div>

      {/* Soft limit warning — one slot left */}
      {nearLimit && (
        <div className="mb-4 flex items-center gap-3 rounded-[14px] bg-amber-50 border border-amber-200 px-4 py-3">
          <span className="text-base flex-shrink-0">⚠️</span>
          <p className="font-body text-sm text-amber-800">
            You're using {contacts.length} of {contactLimit} contact slots.{' '}
            <button
              type="button"
              onClick={() => navigate('/pricing?reason=contact_limit')}
              className="font-semibold underline hover:no-underline"
            >
              Upgrade to add more people.
            </button>
          </p>
        </div>
      )}

      {/* At-limit warning */}
      {atLimit && (
        <div className="mb-4 flex items-center gap-3 rounded-[14px] bg-orange-50 border border-orange-200 px-4 py-3">
          <span className="text-base flex-shrink-0">🔒</span>
          <p className="font-body text-sm text-orange-800">
            You've reached your {contactLimit}-contact limit on the {profile?.tier === 1 ? 'Thoughtful' : 'Connected'} plan.{' '}
            <button
              type="button"
              onClick={() => navigate('/pricing?reason=contact_limit')}
              className="font-semibold underline hover:no-underline"
            >
              Upgrade to add more.
            </button>
          </p>
        </div>
      )}

      {/* Delete error */}
      {deleteError && (
        <div className="mb-4 flex items-center gap-3 rounded-[14px] bg-red-50 border border-red-100 px-4 py-3">
          <svg className="h-4 w-4 flex-shrink-0 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
          <p className="font-body text-sm text-red-600">{deleteError}</p>
        </div>
      )}

      {/* Auto-occasion success hint */}
      {autoHint && (
        <div className="mb-4 flex items-center gap-3 rounded-[14px] bg-sage/10 border border-sage/20 px-4 py-3">
          <svg className="h-4 w-4 flex-shrink-0 text-sage" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          <p className="font-body text-sm text-sage">{autoHint}</p>
          <button
            type="button"
            onClick={() => setAutoHint(null)}
            className="ml-auto text-sage/60 hover:text-sage transition-colors"
            aria-label="Dismiss"
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {contacts.length === 0 ? (
        <div className="rounded-[20px] bg-white shadow-warm px-8 py-12 text-center">
          <div className="text-5xl mb-4">👥</div>
          <h3 className="font-heading text-lg font-semibold text-navy mb-2">
            Add your first person
          </h3>
          <p className="font-body text-sm text-gray-500 mb-6 max-w-xs mx-auto leading-relaxed">
            Tell us who to remember. We'll take care of the cards.
          </p>
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            className="btn-primary text-sm px-8"
          >
            Add someone
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...contacts]
            .sort((a, b) => {
              const af = a.is_favorite ? 1 : 0;
              const bf = b.is_favorite ? 1 : 0;
              if (bf !== af) return bf - af; // favorites first
              return a.name.localeCompare(b.name);
            })
            .map((c) => (
            <ContactCard
              key={c.id}
              contact={c}
              onEdit={() => setEditTarget(c)}
              onAddOccasion={() => setOccasionTarget(c)}
              onDelete={() => handleDelete(c.id)}
              onFavoriteToggled={onContactUpdated}
              onOccasionUpdated={onOccasionUpdated}
              onOccasionDeleted={onOccasionDeleted}
            />
          ))}

          {/* Inline "add" card */}
          {!atLimit && (
            <button
              type="button"
              onClick={handleAddPersonClick}
              className="rounded-[20px] border-2 border-dashed border-sage/25 py-8 flex flex-col items-center justify-center gap-2 text-sage hover:border-sage/50 hover:bg-sage/5 transition-all"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
              <span className="font-body text-sm font-medium">Add person</span>
            </button>
          )}
        </div>
      )}

      {/* ── Modals ─────────────────────────────────────────────────────── */}

      <Modal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        title="Add a person"
      >
        <ContactForm
          onSave={(c, autoOccasions) => {
            onContactAdded(c, autoOccasions);
            setAddOpen(false);
            if (autoOccasions && autoOccasions.length > 0) {
              const labels = autoOccasions
                .map((o) => AUTO_OCCASION_LABELS[o.occasion_type as OccasionType] ?? o.occasion_type)
                .join(' & ');
              setAutoHint(`We also added a ${labels} reminder for ${c.name}.`);
              setTimeout(() => setAutoHint(null), 6000);
            }
          }}
          onCancel={() => setAddOpen(false)}
          onLimitReached={() => {
            setAddOpen(false);
            navigate('/pricing?reason=contact_limit');
          }}
        />
      </Modal>

      <Modal
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        title={`Edit ${editTarget?.name ?? ''}`}
      >
        {editTarget && (
          <ContactForm
            key={editTarget.id}
            contactId={editTarget.id}
            initial={editInitial(editTarget)}
            initialAvatarUrl={editTarget.avatar_url}
            initialBirthday={editInitialBirthday(editTarget)}
            initialAnniversary={editInitialAnniversary(editTarget)}
            initialIsParent={editTarget.is_parent}
            initialParentHoliday={editTarget.parent_holiday ?? null}
            onSave={(c, autoOccasions) => {
              onContactUpdated(c);
              autoOccasions?.forEach((o) => onOccasionAdded(o));
              setEditTarget(null);
            }}
            onCancel={() => setEditTarget(null)}
            submitLabel="Save changes"
          />
        )}
      </Modal>

      <Modal
        open={!!occasionTarget}
        onClose={() => setOccasionTarget(null)}
        title={`Add occasion for ${occasionTarget?.name ?? ''}`}
      >
        {occasionTarget && (
          <OccasionForm
            key={occasionTarget.id}
            contactId={occasionTarget.id}
            contactName={occasionTarget.name}
            hasAddress={!!(occasionTarget.address_street && occasionTarget.address_city)}
            onSave={(o) => { onOccasionAdded(o); setOccasionTarget(null); }}
            onCancel={() => setOccasionTarget(null)}
          />
        )}
      </Modal>
    </section>
  );
}
